<template>
  <v-container fluid>
    <!-- Delete dialog -->
    <alert-dialog
      :is-open="isDeleteDialogOpen"
      :ok-btn-text="$t('actions.delete')"
      @onOk="runDeleteWorkpackage"
      @onCancel="closeDeleteDialog"
    >
      <template v-slot:header>{{ $t('dialogs.deleteWorkpackage.header') }}</template>
      <template v-slot:body>
        {{ $t('dialogs.deleteWorkpackage.body') }}
        <strong>{{ workpackageToDelete ? workpackageToDelete.description : '' }}</strong>
      </template>
    </alert-dialog>

    <!-- Transfer dialog -->
    <transfer-dialog
      :is-open="isTransferDialogOpen"
      :workpackage-to-transfer="workpackageToTransfer"
      @onCancel="closeTransferDialog"
      @onOk="runTransferWorkpackage"
    />

    <!-- clone confirm dialog visible to OW users only-->
    <confirm-dialog
      v-if="isOWUser"
      :is-open="isCloneConfirmDialogOpen"
      :ok-btn-text="$t('actions.confirm')"
      @onOk="confirmClone"
      @onCancel="closeCloneDialog"
    >
      <template v-slot:body>
        <div>
          {{
            $t('dialogs.cloneWPDialog', { workpackageDescription: workpackageToClone.description })
          }}
        </div>
      </template>
    </confirm-dialog>

    <!-- ExportPricing dialog -->
    <alert-dialog
      :is-open="isExportPricingDialogOpen"
      :is-max-height-fixed="true"
      :ok-btn-text="
        useSapExport
          ? $t(
              `workPackages.exportToSAP.${
                sapExportParams.toFTP ? 'exportToSystem' : 'downloadFile'
              }`
            )
          : $t('actions.downloadPricing')
      "
      @onOk="runExport"
      @onCancel="closeExportPricingDialog"
    >
      <template v-slot:header>
        {{
          useSapExport ? $t('workPackages.exportToSAP.title') : $t('dialogs.downloadPricing.header')
        }}
      </template>
      <template v-slot:body>
        <div v-if="!workpackageToExportPricing || exporting(workpackageToExportPricing)">
          {{ $t('general.loadingMessage') }}
        </div>
        <div v-else>
          <div v-if="noProductsGoLiveAfterWorkpackage">
            {{ $t('dialogs.downloadPricing.noProductsGoLiveAfterWP') }}
          </div>
          <div v-else>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span class="panel-header">
                    {{ $t('dialogs.downloadPricing.hasProductsGoLiveAfterWP') }}
                  </span>
                  <template v-slot:actions>
                    <v-icon>expand_more</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p
                    v-for="(productKey, index) in sortedProductsGoLiveAfterWorkpackage"
                    :key="`productKey-${index}`"
                    class="mb-1"
                  >
                    {{ productKey }}
                  </p>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          <br />

          <feature-toggle :toggle="useManualProductGoLiveDate">
            <div v-if="noProductsGoLiveBeforeWorkpackage">
              {{ $t('dialogs.downloadPricing.noProductsGoLiveBeforeWP') }}
            </div>
            <div v-else>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="panel-header">
                      {{ $t('dialogs.downloadPricing.hasProductsGoLiveBeforeWP') }}
                    </span>
                    <template v-slot:actions>
                      <v-icon>expand_more</v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <p
                      v-for="(productKey, index) in sortedProductsGoLiveBeforeWorkpackage"
                      :key="`productKey-${index}`"
                      class="mb-1"
                    >
                      {{ productKey }}
                    </p>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            <br />

            <div v-if="noProductsGoLiveBeforeToday">
              {{ $t('dialogs.downloadPricing.noProductsGoLiveBeforeToday') }}
            </div>
            <div v-else>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="panel-header">
                      {{ $t('dialogs.downloadPricing.hasProductsGoLiveBeforeToday') }}
                    </span>
                    <template v-slot:actions>
                      <v-icon>expand_more</v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <p
                      v-for="(productKey, index) in sortedProductsGoLiveBeforeToday"
                      :key="`productKey-${index}`"
                      class="mb-1"
                    >
                      {{ productKey }}
                    </p>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </feature-toggle>
        </div>
      </template>
    </alert-dialog>

    <!-- Export API dialog -->
    <alert-dialog
      :is-open="isExportAPIDialogOpen"
      :ok-btn-text="$t('actions.confirm')"
      :disable-ok-button="!isAPIExportEligible"
      @onOk="handleExportToAPI"
      @onCancel="closeExportAPIDialog"
    >
      <template v-slot:header>{{ $t('dialogs.exportToDownstreamSystem.header') }}</template>
      <template v-slot:body>
        <p id="export-api-warning" class="dialog-warning">
          <strong>{{ $t('dialogs.exportToDownstreamSystem.warning') }}</strong>
        </p>
        <p>
          <span v-if="formattedAPIBodyText.plain">
            {{ formattedAPIBodyText.plain }}
            <strong v-if="formattedAPIBodyText.bold">{{ formattedAPIBodyText.bold }}</strong>
          </span>
        </p>
        <v-alert v-if="!isAPIExportEligible" type="error" outlined>
          {{ $t('workPackages.noApprovedPricingGroups') }}
        </v-alert>
      </template>
    </alert-dialog>

    <!-- edit reference date dialog -->
    <alert-dialog
      :is-open="isEditReferenceDateDialogOpen"
      :ok-btn-text="$t('actions.save')"
      @onOk="updateReferenceDate(workpackageToEdit, referenceDate)"
      @onCancel="closeEditReferenceDateDialog"
    >
      <template v-slot:header>{{ $t('dialogs.editReferenceDate.header') }}</template>
      <template v-slot:body>
        <p id="dialog-warning" class="mb-2">
          <strong>{{ $t('dialogs.editReferenceDate.warning') }}</strong>
        </p>
        <p>
          {{
            $t(referenceDateUpdateTranslationKey, {
              workpackageDescription: workpackageToEdit ? workpackageToEdit.description : '',
              referenceDate: formatLocalisedDate(referenceDate),
            })
          }}
        </p>
      </template>
    </alert-dialog>

    <v-row>
      <v-col cols="12" class="heading-bar mb-5">
        <div class="d-flex justify-space-between heading-bar-heading">
          <span>{{ $t(heading) }}</span>
          <span>
            <v-icon size="3rem" @click="toggleWorkpackagesExpansion()">
              {{ getExpansionIcon }}
            </v-icon>
          </span>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <div class="flex scroll">
        <v-data-table
          v-if="workpackagesExpanded"
          :class="['work-package-table', { 'reference-date-enabled': enableReferenceDate }]"
          :headers="headers"
          :items="allWorkpackages"
          :search="search"
          :loading-text="$t('general.loadingMessage')"
          :loading="inactiveWps ? inactiveLoading : loading"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.sortDesc"
          max-height="60rem"
          hide-default-footer
          disable-pagination
          item-key="_id"
          @scroll.native="handleScroll"
        >
          <template v-slot:no-data>
            <div class="no-data-text">
              <span v-if="!loading" :value="true">
                {{ $t('workPackages.noData') }}
              </span>
            </div>
          </template>
          <template v-slot:item="props">
            <tr
              :class="[isSelected(props.item), isActive(props.item.goLiveDate, props.item.mocked)]"
              class="workpackage white-row"
            >
              <td class="text-xs-left text-bold">
                <!-- mocked is derived prop of workpackagesInCreation -->
                <progress-spinner
                  v-if="props.item.mocked"
                  :job-id="props.item.description"
                  :job-type="
                    props.item.isCloneJob ? 'run_clone_workpackage' : createWorkpackageWorkflowName
                  "
                />

                <div class="workpackage-description-row">
                  <div class="workpackage-description-content">
                    <v-text-field
                      v-if="canUpdateWorkpackageDescription(props.item)"
                      v-model.trim="editDescriptionBuffer"
                      :label="$t('workPackages.description')"
                      single-line
                      :rules="rules.missingDescription"
                      @blur="
                        updateWorkpackageDescription({
                          workpackage: props.item,
                          description: editDescriptionBuffer,
                        })
                      "
                    />
                    <tooltip v-else :value="props.item.description" position="top">
                      <a
                        class="link-arrow pb-2"
                        e2e-slug="linkWP"
                        @click="selectWorkpackage(props.item)"
                      >
                        {{
                          props.item.description
                            | truncate(getWorkpackageDescriptionTruncationLength(props.item))
                        }}
                      </a>
                    </tooltip>
                  </div>
                  <feature-toggle toggle="enableWorkpackagePeriodTypes">
                    <v-tooltip v-if="getPeriodTypeBadge(props.item)" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="period-type-badge ml-1" v-bind="attrs" v-on="on">{{
                          getPeriodTypeBadge(props.item).label
                        }}</span>
                      </template>
                      <span>{{ getPeriodTypeBadge(props.item).tooltip }}</span>
                    </v-tooltip>
                  </feature-toggle>
                </div>

                <work-package-reveal v-if="isExpanded(props.item._id)" :workpackage="props.item" />
              </td>
              <td :colspan="getColSpan(2)" class="pa-0 col-border-right content-end">
                <table class="inner-table">
                  <tbody>
                    <tr>
                      <feature-toggle toggle="enableReferenceDate">
                        <td class="pa-1 text-small col-fixed-width col-no-border">
                          <v-menu
                            v-if="canUpdateReferenceDate(props.item)"
                            v-model="referenceDatePickerEdit"
                            :close-on-content-click="false"
                            :nudge-right="5"
                            transition="scale-transition"
                            offset-y
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                :value="computedReferenceDate"
                                class="edit-reference-date"
                                append-icon="event"
                                readonly
                                clearable
                                :loading="refreshing(props.item)"
                                :disabled="disabled(props.item)"
                                v-on="on"
                                @click:append="referenceDatePickerEdit = true"
                                @click:clear="clearReferenceDateAndOpenDialog(props.item)"
                              />
                            </template>
                            <v-date-picker
                              v-model="referenceDate"
                              :first-day-of-week="i18nconfig.firstDayOfTheWeek"
                              :locale="i18nconfig.fallbackLocale"
                              :min="sixMonthsAgo"
                              :max="todaysDate"
                              @input="openEditReferenceDateDialog(props.item)"
                            />
                          </v-menu>
                          <template v-else>
                            {{ getReferenceDate(props.item.referenceDate) }}
                          </template>
                        </td>
                      </feature-toggle>
                      <td
                        class="pa-1 text-small col-fixed-width col-no-border"
                        :class="{
                          'match-text-input-padding-top':
                            isExpanded(props.item._id) && enableReferenceDate && canEditWorkpackage,
                        }"
                      >
                        {{ getCompletionDate(props.item.completionDate) }}
                      </td>
                      <td
                        class="pa-1 text-small col-fixed-width col-no-border"
                        :class="{
                          'match-text-input-padding-top':
                            isExpanded(props.item._id) && enableReferenceDate && canEditWorkpackage,
                        }"
                      >
                        {{ formatLocalisedDate(props.item.goLiveDate) }}
                      </td>
                    </tr>
                    <tr>
                      <td :colspan="getColSpan(2)" class="pl-1 pb-0 text-small col-no-border">
                        <span>
                          <tooltipped-truncated-field
                            :text="
                              props.item.sourceWorkpackageDescription === undefined
                                ? props.item.storeFormat
                                : props.item.sourceWorkpackageDescription
                            "
                            :truncation-length="35"
                          />
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td
                v-for="(unit, i) in getUnitsFromWorkPackage(props.item)"
                :key="`workPackagesUnit${i}`"
                class="col-border-right col-unit pr-0"
              >
                <work-packages-unit
                  :unit-data="unit"
                  :is-expanded="isExpanded(props.item._id)"
                  :is-selectable="false"
                  :prevent-unit-selection="false"
                />
              </td>
              <!-- Fill in the gaps with blank units -->
              <td
                v-for="n in maxUnitCount - getUnitsFromWorkPackage(props.item).length"
                :key="`blankCell${n}`"
                class="col-border-right col-unit"
              />
              <td class="col-border-left content-end pl-0 pr-0">
                <v-icon
                  v-if="isCreateWorkpackageFromCopyEnabled && allowCopy && canEditWorkpackage"
                  :disabled="disabled(props.item)"
                  color="primary"
                  class="pl-1"
                  @click="openWorkpackageCopyDialog(props.item)"
                >
                  mdi-content-copy
                </v-icon>
                <v-icon
                  v-if="!isExpanded(props.item._id)"
                  class="pb-1"
                  large
                  e2e-slug="iconExpandWP"
                  @click="toggleExpansion(props.item._id)"
                >
                  expand_more
                </v-icon>
                <v-icon
                  v-if="isExpanded(props.item._id)"
                  class="pb-1"
                  large
                  e2e-slug="iconCollapseWP"
                  @click="toggleExpansion(props.item._id)"
                >
                  expand_less
                </v-icon>
              </td>
            </tr>
            <tr v-if="isExpanded(props.item._id) && canExecuteWorkpackage" class="button-row">
              <!-- Using colspan here breaks sticky cells -->
              <td class="text-left pt-2 pb-2">
                <v-btn
                  small
                  :disabled="disabled(props.item)"
                  :loading="transferring(props.item)"
                  color="primary"
                  e2e-slug="btnTransferWorkpackage"
                  depressed
                  @click="openTransferDialog(props.item)"
                >
                  {{ $t('workPackages.transferWorkpackage') }}
                </v-btn>

                <v-btn
                  v-if="isOWUser"
                  small
                  class="ml-3"
                  color="primary"
                  depressed
                  @click="openCloneDialog(props.item)"
                >
                  {{ $t('workPackages.cloneWorkpackage') }}
                </v-btn>
              </td>

              <td :colspan="maxUnitCount" />
              <td :colspan="getColSpan(4)" class="pt-2 pb-2 text-right">
                <feature-toggle toggle="displayExportPenaltyDataButton">
                  <v-btn
                    small
                    color="primary"
                    :loading="loadingPenaltyExports"
                    class="ml-2"
                    depressed
                    @click="
                      exportPenaltyData({
                        workpackage: props.item,
                      })
                    "
                  >
                    {{ $t('actions.downloadPenaltyData') }}
                  </v-btn>
                </feature-toggle>
                <feature-toggle toggle="displayExportToSAPButton">
                  <div class="api-export-container d-inline-flex align-center">
                    <div class="last-export-info mr-2">
                      <div
                        v-if="hasExportInfo(props.item._id, exportType.exportToSAP)"
                        class="d-flex align-center"
                      >
                        {{ getExportLastText(props.item._id, exportType.exportToSAP) }}
                      </div>
                    </div>
                    <sap-export-button
                      :disabled="disabled(props.item) || disableOWUserExport"
                      :loading="exportingToSAP(props.item)"
                      :params="{
                        selectedWorkpackage: props.item,
                        fieldConfigs: exportToSAPFields,
                        exportConfig: exportConfigs.exportToSAP,
                        exportType: exportType.exportToSAP,
                      }"
                      @downloadExportToSAP="triggerDownloadExportToSAP"
                    />
                  </div>
                </feature-toggle>
                <feature-toggle toggle="displayExportToAPIButton">
                  <div class="api-export-container d-inline-flex align-center">
                    <div class="last-export-info mr-2">
                      <div v-if="hasExportInfo(props.item._id)" class="d-flex align-center">
                        {{ getExportLastText(props.item._id) }}
                      </div>
                    </div>
                    <v-btn
                      small
                      :disabled="disabled(props.item) || disableOWUserExport"
                      :loading="posting(props.item)"
                      color="primary"
                      class="ml-2"
                      e2e-slug="btnPostPricing"
                      depressed
                      @click="openExportAPIDialog(props.item)"
                    >
                      {{ $t('actions.postPricing') }}
                    </v-btn>
                  </div>
                </feature-toggle>
                <!-- XSR export uses downloadFile option in sap-export-button -->
                <span v-if="displayGoLiveDateDialog">
                  <v-btn
                    small
                    :disabled="disabled(props.item)"
                    :loading="exporting(props.item)"
                    color="primary"
                    class="ml-2"
                    e2e-slug="btnExportPricing"
                    depressed
                    @click="openExportPricingDialog(props.item)"
                  >
                    {{ $t('actions.downloadPricing') }}
                  </v-btn>
                </span>
                <span v-else-if="!isXSRClient">
                  <v-btn
                    small
                    :disabled="disabled(props.item)"
                    :loading="exporting(props.item)"
                    color="primary"
                    class="ml-2"
                    e2e-slug="btnExportPricing"
                    depressed
                    @click="
                      downloadWorkpackageResults({
                        selectedWorkpackage: props.item,
                        fields,
                        exportType: exportType.exportPricing,
                        columnFormatters,
                      })
                    "
                  >
                    {{ $t('actions.downloadPricing') }}
                  </v-btn>
                </span>

                <v-tooltip bottom :disabled="!props.item.isExecuted">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-btn
                        v-if="props.item.type !== workpackageTypes.master"
                        small
                        color="primary"
                        class="ml-2"
                        :disabled="deleteDisabled(props.item)"
                        :loading="deleting(props.item)"
                        e2e-slug="btnDeleteWP"
                        depressed
                        @click="openDeleteDialog(props.item)"
                      >
                        <v-icon small> fa-trash </v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ $t('tooltip.executedWorkpackageDelete') }}</span>
                </v-tooltip>
              </td>
            </tr>
            <tr class="spacer-row">
              <td />
              <td :colspan="getColSpan(2)" />
              <td :colspan="maxUnitCount + 1" />
            </tr>
          </template>
          <template v-slot:body.prepend="{}">
            <tr v-if="searchWorkpackages" class="white-row">
              <td :colspan="getColSpan(2)" class="search-container">
                <v-text-field
                  class="search-field p-0"
                  :value="search"
                  append-icon="search"
                  :label="$t('actions.search')"
                  single-line
                  :loading="loading"
                  @input="debounceSearchUpdate"
                />
              </td>
            </tr>
            <tr
              v-show="creationMode && canEditWorkpackage"
              class="workpackage white-row new-workpackage"
            >
              <td class="text-xs-left">
                <v-text-field
                  v-model.trim="newWorkpackage.description"
                  :label="$t('workPackages.description')"
                  single-line
                  class="ma-0 pa-0"
                  :rules="rules.missingDescription"
                />
                {{ $t('workPackages.owner') }}: <strong>{{ profile && profile.username }}</strong>
                <feature-toggle toggle="enableWorkpackagePeriodTypes">
                  <v-select
                    v-model="newWorkpackage.workpackagePeriodType"
                    :items="workpackagePeriodTypeItems"
                    :label="`${$t('workPackages.workpackagePeriodType')} *`"
                    :rules="[valueRequired]"
                    item-text="text"
                    item-value="value"
                    class="mt-2"
                    dense
                  />
                </feature-toggle>
                <feature-toggle toggle="enableAOExtracts">
                  <div class="ao-extracts">
                    <v-autocomplete
                      v-model="selectedAOExtracts"
                      :items="aoExtractsOptions"
                      class="mt-6"
                      multiple
                      dense
                      item-text="displayName"
                      item-value="auditCorrelationId"
                      :label="$t('workPackages.aoExtracts')"
                      :rules="[aoExtractsRules]"
                    />
                    <feature-toggle toggle="usePeriodIdsInAOExtracts">
                      <template v-if="selectedAOExtracts.length > 0">
                        <div>{{ $t('workPackages.associatedPeriodIds') }}</div>
                        <div>{{ getAssociatedPeriodIds(selectedAOExtracts) }}</div>
                      </template>
                    </feature-toggle>
                  </div>
                </feature-toggle>

                <cost-threshold-inputs
                  :workpackage="newWorkpackage"
                  @validation-change="hasThresholdValidationError = $event"
                />

                <optimise-prices-toggle />
                <refresh-on-create-toggle />
              </td>
              <td :colspan="getColSpan(2)" class="pa-0 col-border-right content-end">
                <table class="inner-table">
                  <tbody>
                    <tr>
                      <feature-toggle toggle="enableReferenceDate">
                        <td class="pa-1 text-small col-fixed-width col-no-border">
                          <v-menu
                            v-model="referenceDatePicker"
                            :close-on-content-click="false"
                            :nudge-right="5"
                            transition="scale-transition"
                            offset-y
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                :value="computedReferenceDate"
                                class="edit-reference-date"
                                append-icon="event"
                                readonly
                                clearable
                                v-on="on"
                                @click:append="referenceDatePicker = true"
                                @click:clear="clearReferenceDate"
                              />
                            </template>
                            <v-date-picker
                              v-model="referenceDate"
                              :first-day-of-week="i18nconfig.firstDayOfTheWeek"
                              :locale="i18nconfig.fallbackLocale"
                              :min="sixMonthsAgo"
                              :max="todaysDate"
                              @input="referenceDatePicker = false"
                            />
                          </v-menu>
                        </td>
                      </feature-toggle>
                      <td class="pa-1 text-small col-fixed-width col-no-border">
                        <v-menu
                          v-model="completionDatePicker"
                          :close-on-content-click="false"
                          :nudge-right="5"
                          transition="scale-transition"
                          offset-y
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              v-model="computedCompletionDate"
                              append-icon="event"
                              readonly
                              v-on="on"
                              @click:append="completionDatePicker = true"
                            />
                          </template>
                          <v-date-picker
                            v-model="completionDate"
                            :first-day-of-week="i18nconfig.firstDayOfTheWeek"
                            :locale="i18nconfig.fallbackLocale"
                            @input="completionDatePicker = false"
                          />
                        </v-menu>
                      </td>
                      <td class="pa-1 text-small col-fixed-width col-no-border">
                        <v-menu
                          v-model="goLiveDatePicker"
                          :close-on-content-click="false"
                          :nudge-right="5"
                          transition="scale-transition"
                          offset-y
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              v-model="computedGoLiveDate"
                              append-icon="event"
                              readonly
                              v-on="on"
                              @click:append="goLiveDatePicker = true"
                            />
                          </template>
                          <v-date-picker
                            v-model="goLiveDate"
                            :first-day-of-week="i18nconfig.firstDayOfTheWeek"
                            :locale="i18nconfig.fallbackLocale"
                            @input="onGoLiveDateInput()"
                          />
                        </v-menu>
                      </td>
                    </tr>
                    <tr>
                      <td :colspan="getColSpan(2)" class="pl-1 pb-0 text-small col-no-border">
                        <v-autocomplete
                          v-model="newWorkpackage.workpackageId"
                          :label="$t('general.labels.selectDefault')"
                          :items="sourceWorkpackages"
                          :rules="rules.hasValue"
                          single-line
                          item-text="description"
                          item-value="_id"
                          @change="setAvailableHierarchiesAndStoreFormat($event)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <template v-if="creationHierarchyLevel === pricingGroupLevel">
                <td class="col-border-right col-unit pr-0">
                  <v-autocomplete
                    v-model="newWorkpackage.hierarchyIds"
                    :items="availablePricingGroups"
                    item-text="levelEntryDescription"
                    item-value="levelEntryKey"
                    multiple
                    dense
                    flat
                    small-chips
                    deletable-chips
                    clearable
                    class="pg-autocomplete"
                    :label="$t('pricing.pricingGroup')"
                    :menu-props="{ offsetY: true }"
                  />
                </td>
              </template>
              <template v-if="creationHierarchyLevel === categoryLevel">
                <td
                  v-for="(category, i) in availableCategories"
                  :key="`category${i}`"
                  class="col-border-right col-unit pr-0"
                >
                  <work-packages-unit
                    :unit-data="category"
                    :is-expanded="true"
                    :is-selectable="true"
                    :prevent-unit-selection="true"
                    @onCategoriesUpdated="setCategories"
                  />
                </td>
                <!-- Fill in the gaps with blank units -->
                <td
                  v-for="n in maxUnitCount - availableCategories.length"
                  :key="`blankCell${n}`"
                  class="col-border-right col-unit"
                />
                <td class="col-border-left content-end pl-0 pr-0" />
              </template>
            </tr>
            <tr v-show="creationMode && canEditWorkpackage" class="button-row">
              <!-- Using colspan here breaks sticky cells -->
              <td />
              <td :colspan="maxUnitCount + 1" />
              <td :colspan="getColSpan(2)" class="pt-2 pb-2 new-workpackage-buttons">
                <v-btn color="primary" outlined small depressed @click="cancelNewWorkPackage()">
                  {{ $t('actions.cancel') }}
                </v-btn>
                <v-btn
                  small
                  :disabled="!isValid"
                  :loading="creating"
                  class="save"
                  color="success"
                  depressed
                  @click="saveNewWorkPackage()"
                >
                  {{ $t('actions.save') }}
                </v-btn>
              </td>
            </tr>
            <tr v-show="creationMode && canEditWorkpackage" class="spacer-row">
              <td />
              <td :colspan="getColSpan(2)" />
              <td :colspan="maxUnitCount + 1" />
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-row>
    <v-row class="pt-2 pr-2">
      <plus-button
        v-if="canCreateWorkpackage"
        copy="workPackages.createNew"
        e2e-slug="btnCreateWP"
        @clickFunction="createNewWorkPackage"
      />
    </v-row>
    <copy-workpackage-dialog
      v-if="isCreateWorkpackageFromCopyEnabled && allowCopy && canEditWorkpackage"
      :is-open="isCopyWorkpackageDialogOpen"
      :options="copyWorkpackageOptions"
      @onOk="confirmCopyWorkpackage"
      @onCancel="cancelCopyWorkpackage"
    />
  </v-container>
</template>

<script>
import to from 'await-to-js';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { times, max, clone, isEmpty, sortBy, get, debounce, omit, find, findIndex } from 'lodash';
import moment from 'moment';
import dateHasPassed from '../../../store/utils/date-checking';
import workpackageTypes from '@enums/workpackage-types';
import {
  yearMonthDayFormat,
  utcFormat,
  hoursMinutesDayMonthYearSeparatedFormat,
} from '@enums/date-formats';
import numberFormats from '@enums/number-formats';
import {
  displayExportToSAPButton,
  displayExportToAPIButton,
  owUsersCanExport,
  showGoLiveDateDialog,
  useManualProductGoLiveDate,
  enableAOExtracts,
  usePeriodIdsInAOExtracts,
  workpackageCreationHierarchyLevel,
  createWorkpackageFromCopy,
  enableReferenceDate,
  enableCompetitorSimulation,
  refreshWorkpackageFromDatabricks,
  defaultCostChangeThresholdMin,
  defaultCostChangeThresholdMax,
  enableWorkpackagePeriodTypes,
} from '@enums/feature-flags';
import workpackagePeriodTypes from '@enums/workpackage-period-types';
import { pricingGroupLevel, categoryLevel } from '@enums/hierarchy';
import exportType from '@enums/export-type';
import sapExportUtilsMixin from '../../../mixins/sapExportUtils';
import featureFlagsMixin from '../../../mixins/featureFlags';
import formatLocalisedDateMixin from '../../../mixins/formatLocalisedDate';
import clientConfig from '@sharedModules/config/client';
import Events from '@enums/events';
import axios from 'axios';
import { descending } from '@enums/sort-direction';
import featureToggle from '../../../components/feature-toggle.vue';
import AlertDialog from '../../../components/alert-dialog.vue';
import ConfirmDialog from '../../../components/confirm-dialog.vue';

const defaultCopyWorkpackageOptions = () => ({
  source: {},
  target: {},
  reuseExistingAOExtracts: true,
  selectedAOExtracts: [],
});

const todaysDate = moment().format('YYYY-MM-DD');
const sixMonthsAgo = moment()
  .subtract(6, 'months')
  .format('YYYY-MM-DD');
const defaultWorkpackagePeriodType = workpackagePeriodTypes.aoPeriod;

export default {
  components: { featureToggle, AlertDialog, ConfirmDialog },
  mixins: [sapExportUtilsMixin, featureFlagsMixin, formatLocalisedDateMixin],
  props: {
    heading: {
      type: String,
      required: true,
    },
    filteredWorkpackages: {
      type: Array,
      required: true,
    },
    sortDesc: {
      type: Boolean,
      required: true,
    },
    allowNew: {
      type: Boolean,
      required: true,
    },
    allowCopy: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    searchWorkpackages: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      search: '',
      // mutating descriptions directly will cause rows to resort on edit. add buffer to avoid this bug.
      editDescriptionBuffer: '',
      workpackageTypes,
      rules: {
        missingDescription: [this.valueRequired],
        hasValue: [this.hasValue],
      },
      exportStatus: null,
      allWorkpackages: this.filteredWorkpackages,
      creationMode: false,
      completionDatePicker: false,
      goLiveDatePicker: false,
      referenceDatePicker: false,
      referenceDatePickerEdit: false,
      goLiveDate: new Date().toISOString().substr(0, 10),
      completionDate: new Date().toISOString().substr(0, 10),
      referenceDate: null,
      todaysDate,
      sixMonthsAgo,
      availablePricingGroups: [],
      availableCategories: [],
      selectedCategories: {},
      newWorkpackage: {},
      hasThresholdValidationError: false,
      isDeleteDialogOpen: false,
      isTransferDialogOpen: false,
      isExportPricingDialogOpen: false,
      isExportAPIDialogOpen: false,
      isEditReferenceDateDialogOpen: false,
      workpackageToDelete: undefined,
      workpackageToEdit: undefined,
      workpackageToTransfer: {},
      workpackageToExportPricing: undefined,
      workpackageToExportAPI: undefined,
      workpackagesExpanded: this.expanded,
      sorting: {},
      enableAOExtracts,
      usePeriodIdsInAOExtracts,
      displayExportToSAPButton,
      displayExportToAPIButton,
      exportType,
      unitsToDisplay: 15,
      useManualProductGoLiveDate,
      useSapExport: false,
      sapExportParams: {},
      loadingPenaltyExports: false,
      selectedAOExtracts: [],
      pricingGroupLevel,
      categoryLevel,
      exportLastInfo: {}, // format: {workpackageId: {api: {...}, sap: {...}}}
      isExportLastInfoLoading: {}, // format: {workpackageId: {api: boolean, sap: boolean}}
      isCopyWorkpackageDialogOpen: false,
      copyWorkpackageOptions: defaultCopyWorkpackageOptions(),
      isAPIExportEligible: false,
      isCloneConfirmDialogOpen: false,
      workpackageToClone: {},
    };
  },

  computed: {
    ...mapState('context', ['profile']),
    ...mapState('hierarchy', ['hierarchy']),
    ...mapState('clientConfig', ['i18nconfig', 'exportConfigs', 'toggleLogic']),
    ...mapState('workpackages', [
      'loading',
      'inactiveLoading',
      'workpackages',
      'selectedWorkpackage',
      'creating',
      'deletingIds',
      'exportingIds',
      'postingIds',
      'exportingToSAPIds',
      'refreshingIds',
      'productsGoLiveBeforeToday',
      'productsGoLiveBeforeWorkpackage',
      'productsGoLiveAfterWorkpackage',
      'workpackageTruncationLength',
    ]),
    ...mapGetters('workpackages', [
      'masterWorkpackages',
      'activeWorkpackages',
      'archivedWorkpackages',
      'isAnyWorkpackageInTransferList',
      'getAvailableAOExtracts',
      'getAOExtractsByKey',
      'getAssociatedPeriodIds',
      'isAOExtractsValid',
      'createWorkpackageWorkflowName',
      'calculateCompletionDate',
    ]),
    ...mapGetters('context', [
      'isUnitManager',
      'isAdminLoggedIn',
      'isOWUser',
      'canExecuteWorkpackage',
      'canEditWorkpackage',
    ]),
    ...mapGetters('hierarchy', ['getHierarchyForWorkpackage']),

    staticHeaders() {
      return [
        {
          text: this.$t('workPackages.description'),
          value: 'description',
          align: 'left',
          sortable: true,
        },
        ...(this.enableReferenceDate
          ? [
              {
                text: this.$t('workPackages.referenceDate'),
                value: 'referenceDate',
                align: 'left',
                class: 'col-fixed-width',
                sortable: true,
              },
            ]
          : []),
        {
          text: this.$t('workPackages.completionDate'),
          value: 'completionDate',
          align: 'left',
          class: 'col-fixed-width',
          sortable: true,
        },
        {
          text: this.$t('workPackages.goLiveDate'),
          value: 'goLiveDate',
          align: 'left',
          class: 'col-fixed-width col-border-right content-end',
          sortable: true,
        },
      ];
    },

    headers() {
      const unitsHeaders = [];
      times(this.maxUnitCount, i => {
        unitsHeaders.push({
          text: this.$tc(`hierarchy.hierarchyLevels.1`, 1),
          value: `unit${i}`,
          align: 'left',
          sortable: false,
          class: i === this.maxUnitCount - 1 ? 'content-end' : '',
        });
      });
      return [
        ...this.staticHeaders,
        ...unitsHeaders,
        {
          text: '',
          value: `reveal`,
          align: 'left',
          sortable: false,
          class: 'content-end col-border-left',
        },
      ];
    },

    columnFormatters() {
      return {
        productKeyDisplay: {
          type: get(
            this.exportConfigs,
            'exportToExcel.columnFormatter.productKeyDisplay',
            numberFormats.integer
          ),
        },
      };
    },

    isXSRClient() {
      return this.toggleLogic.workpackageExportFunction === 'exportXSRWorkpackage';
    },

    maxUnitCount() {
      // we're forcing minimum 4 units
      return max([
        4,
        ...this.workpackages.map(workpackage => {
          const retailUnits = get(workpackage, 'units', []).filter(
            unit => unit.wholesale === false
          );
          return max([retailUnits.length, 4]);
        }),
      ]);
    },

    isValid() {
      const hasValidWorkpackagePeriodType =
        !this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes) ||
        !isEmpty(this.newWorkpackage.workpackagePeriodType);

      return (
        !isEmpty(this.newWorkpackage.storeFormat) &&
        !isEmpty(get(this.newWorkpackage, 'description', '').trim()) &&
        !isEmpty(this.newWorkpackage.hierarchyIds) &&
        !isEmpty(this.newWorkpackage.workpackageId) &&
        this.isAOExtractsValid(this.selectedAOExtracts) &&
        hasValidWorkpackagePeriodType &&
        !this.hasThresholdValidationError
      );
    },

    computedCompletionDate() {
      return this.formatLocalisedDate(this.completionDate);
    },

    computedGoLiveDate() {
      return this.formatLocalisedDate(this.goLiveDate);
    },

    computedReferenceDate() {
      return this.referenceDate ? this.formatLocalisedDate(this.referenceDate) : this.referenceDate;
    },

    fields() {
      const results = 'workpackage.results';
      return {
        storeFormat: this.$t(`${results}.storeFormat`),
        region: this.$t(`${results}.region`),
        productKeyDisplay: this.$t(`${results}.productKey`),
        date: this.$t(`${results}.date`),
        dateFormat: this.$t(`${results}.dateFormat`),
        price: this.$t(`${results}.price`),
      };
    },

    apiExportFields() {
      const results = 'workpackage.exports.api';
      return {
        storeFormat: this.$t(`${results}.storeFormat`),
        productKeyDisplay: this.$t(`${results}.productKey`),
        date: this.$t(`${results}.date`),
        dateFormat: this.$t(`${results}.dateFormat`),
        price: this.$t(`${results}.price`),
      };
    },

    exportToSAPFields() {
      return this.getSapExportFields();
    },

    getExpansionIcon() {
      return this.workpackagesExpanded ? 'expand_less' : 'expand_more';
    },

    inactiveWps() {
      return this.heading === 'home.archivedHeading';
    },

    disableOWUserExport() {
      return !this.isFeatureFlagEnabled(owUsersCanExport) && this.isOWUser;
    },

    noProductsGoLiveBeforeToday() {
      return isEmpty(this.productsGoLiveBeforeToday);
    },

    noProductsGoLiveBeforeWorkpackage() {
      return isEmpty(this.productsGoLiveBeforeWorkpackage);
    },

    noProductsGoLiveAfterWorkpackage() {
      return isEmpty(this.productsGoLiveAfterWorkpackage);
    },

    displayGoLiveDateDialog() {
      return !!this.toggleLogic[showGoLiveDateDialog];
    },

    sortedProductsGoLiveBeforeToday() {
      return sortBy(this.productsGoLiveBeforeToday);
    },

    sortedProductsGoLiveBeforeWorkpackage() {
      return sortBy(this.productsGoLiveBeforeWorkpackage);
    },

    sortedProductsGoLiveAfterWorkpackage() {
      return sortBy(this.productsGoLiveAfterWorkpackage);
    },

    creationHierarchyLevel() {
      return this.toggleLogic[workpackageCreationHierarchyLevel];
    },

    canCreateWorkpackage() {
      return this.allowNew && !this.creationMode && this.canEditWorkpackage;
    },

    aoExtractsOptions() {
      return this.getAvailableAOExtracts(this.goLiveDate);
    },

    workpackagePeriodTypeItems() {
      return [
        { text: this.$t('workPackages.periodTypes.weekly'), value: workpackagePeriodTypes.weekly },
        {
          text: this.$t('workPackages.periodTypes.aoPeriod'),
          value: workpackagePeriodTypes.aoPeriod,
        },
      ];
    },

    sourceWorkpackages() {
      if (this.isCreateWorkpackageFromCopyEnabled) {
        return [
          ...this.masterWorkpackages,
          ...this.activeWorkpackages,
          ...this.archivedWorkpackages,
        ];
      }
      return this.masterWorkpackages;
    },

    isCreateWorkpackageFromCopyEnabled() {
      return this.isFeatureFlagEnabled(createWorkpackageFromCopy);
    },

    enableReferenceDate() {
      return this.isFeatureFlagEnabled(enableReferenceDate);
    },

    referenceDateUpdateTranslationKey() {
      // when removing reference date, translation shows 'remove' instead of 'update' to empty
      return this.referenceDate
        ? 'dialogs.editReferenceDate.body'
        : 'dialogs.editReferenceDate.bodyWithoutReferenceDate';
    },

    formattedAPIBodyText() {
      const text = this.$t('dialogs.exportToDownstreamSystem.body', {
        workpackageDescription: this.workpackageToExportAPI
          ? this.workpackageToExportAPI.description
          : '',
      });
      return this.parseFormattedText(text);
    },

    newWorkpackageSource() {
      return find(this.sourceWorkpackages, { _id: this.newWorkpackage.workpackageId });
    },
  },

  watch: {
    filteredWorkpackages(value) {
      // this watcher is needed since Data table isn't notified
      // about updates in allWorkpackages (so that WP content is outdated).

      // note: the allWorkpackages name is misleading.
      // this.workpackages is all workpackages.
      // this.allWorkpackages is workpackages rendered in this component.
      // typically there are 3 instances of this component on the homepage - master, active, archived.
      this.allWorkpackages = value;
    },

    goLiveDate(value) {
      // if sourceWorkpackage has not been selected but date changed, still set refresh flag
      if (value !== get(this.newWorkpackageSource || {}, 'goLiveDate')) {
        this.setCreateWorkpackageRefreshFromFeed(true);
      }
      // set completion date if feature flag is enabled
      this.completionDate = this.getCompletionDateBasedOnGoLiveDate() || this.completionDate;
    },
    referenceDate(value) {
      // if sourceWorkpackage has not been selected but date changed, still set refresh flag
      if (value !== get(this.newWorkpackageSource || {}, 'referenceDate')) {
        this.setCreateWorkpackageRefreshFromFeed(true);
      }
    },
  },

  created() {
    this.sorting = {
      sortBy: ['goLiveDate', 'description'],
      sortDesc: [this.sortDesc, false],
    };
  },

  methods: {
    ...mapActions('workpackages', [
      'setUserSelectionWorkpackage',
      'downloadWorkpackageResults',
      'downloadXSROutboundExport',
      'exportWorkpackageResultsToAPI',
      'downloadExportToSAP',
      'createWorkpackage',
      'deleteWorkpackage',
      'addWorkpackageToExportingList',
      'removeWorkpackageFromExportingList',
      'transferWorkpackage',
      'previewWorkpackageResults',
      'downloadPenaltyExportData',
      'updateWorkpackage',
      'refreshWorkpackage',
      'cloneWorkpackage',
    ]),
    ...mapActions('hierarchy', ['fetchHierarchyForWorkpackage']),
    ...mapActions('storeGroupRelationships', ['fetchStoregroupKeyNameMap']),
    ...mapMutations('workpackages', [
      'setCreateWorkpackageRefreshFromFeed',
      'defaultCreateWorkpackageOptions',
      'setExactCopyWorkpackageOptions',
    ]),

    // when featureFlag is enabled, completionDate is based on config and goLiveDate.
    // allow goLiveDate param to override this.goLiveDate for creation case
    getCompletionDateBasedOnGoLiveDate({ goLiveDate } = {}) {
      return this.calculateCompletionDate({ goLiveDate: goLiveDate || this.goLiveDate });
    },

    // used by parent component home.vue
    hasInvalidWorkpackageDescription() {
      return this.allWorkpackages.some(wp => isEmpty(wp.description));
    },

    debounceSearchUpdate: debounce(function(value) {
      this.search = value;
    }, clientConfig.inputDebounceValue),

    async exportPenaltyData({ workpackage }) {
      this.loadingPenaltyExports = true;
      const [err] = await to(this.downloadPenaltyExportData({ workpackage }));
      if (err) this.loadingPenaltyExports = false;
      this.loadingPenaltyExports = false;
    },

    triggerDownloadExportToSAP(event) {
      // Export function routing is handled by workpackageExportFunction feature flag
      // All exports use SAP-like exportType for business logic (approvals, price filtering)
      if (this.isXSRClient) {
        this.handleExportXSR(event);
      } else if (this.displayGoLiveDateDialog) {
        this.useSapExport = true;
        this.sapExportParams = event;
        const workpackage = event.selectedWorkpackage;
        this.openExportPricingDialog(workpackage);
      } else {
        this.downloadExportToSAP(event);
      }
    },

    async callDownloadExportToSAP() {
      this.isExportPricingDialogOpen = false;
      await this.downloadExportToSAP(this.sapExportParams);
      this.useSapExport = false;
      this.sapExportParams = {};

      await this.fetchExportToSAPLastExportInfo(this.workpackageToExportAPI);
    },

    handleScroll(event) {
      const isUserScrollingHorizontally = event.target.scrollLeft > 0;
      if (isUserScrollingHorizontally) {
        // The Magic Number 9 used below ensures that we never see blank units
        // ie the next units to be shown are loaded before we scroll there
        let percentageScrolled = event.target.scrollLeft / event.target.scrollWidth;
        // event.target.scrollLeft will never equal event.target.scrollWidth
        if (percentageScrolled > 0.9) percentageScrolled = 1;
        const newUnitsToDisplay = Math.round(this.maxUnitCount * percentageScrolled) + 9;
        this.unitsToDisplay =
          newUnitsToDisplay > this.unitsToDisplay - 9 ? newUnitsToDisplay : this.unitsToDisplay;
      }
    },

    deleting(workpackage) {
      return this.deletingIds.includes(workpackage._id);
    },

    transferring(workpackage) {
      return this.isAnyWorkpackageInTransferList([workpackage._id]);
    },

    exportingToSAP(workpackage) {
      return this.exportingToSAPIds.includes(workpackage._id);
    },

    exporting(workpackage) {
      return this.exportingIds.includes(workpackage._id);
    },

    posting(workpackage) {
      return this.postingIds.includes(workpackage._id);
    },

    refreshing(workpackage) {
      return this.refreshingIds.includes(workpackage._id);
    },

    disabled(workpackage) {
      return (
        this.exporting(workpackage) ||
        this.posting(workpackage) ||
        this.exportingToSAP(workpackage) ||
        this.refreshing(workpackage) ||
        this.deleting(workpackage) ||
        this.transferring(workpackage) ||
        workpackage.mocked
      );
    },

    deleteDisabled(workpackage) {
      return this.disabled(workpackage) || workpackage.isExecuted;
    },

    openExportPricingDialog(workpackage) {
      this.workpackageToExportPricing = workpackage;
      const params = {
        selectedWorkpackage: workpackage,
        fields: this.fields,
        exportType: exportType.exportPricing,
      };
      this.previewWorkpackageResults(params);

      this.isExportPricingDialogOpen = true;
    },

    closeExportPricingDialog() {
      this.useSapExport = false;
      this.sapExportParams = {};
      this.isExportPricingDialogOpen = false;
    },

    async openExportAPIDialog(workpackage) {
      this.workpackageToExportAPI = workpackage;
      await this.checkAPIExportApprovalStatus(workpackage);
      this.isExportAPIDialogOpen = true;
    },

    closeExportAPIDialog() {
      this.workpackageToExportAPI = undefined;
      this.isExportAPIDialogOpen = false;
      this.isAPIExportEligible = false; // Reset for next dialog open
    },

    async checkAPIExportApprovalStatus(workpackage) {
      if (!workpackage) return;

      const [err, response] = await to(
        this.$http.get(
          `/api/work-packages/workpackage/${workpackage._id}/check-export-approval-status`
        )
      );

      if (err) {
        console.error('Error checking export approval status', err);
        this.isAPIExportEligible = false; // Disallow export if check fails
        return;
      }

      this.isAPIExportEligible = response.data.eligible;
    },

    async handleExportXSR(event) {
      // Dialog is already closed in sap-export-button component
      const { selectedWorkpackage, exportConfig, toFTP } = event;
      await this.downloadXSROutboundExport({
        selectedWorkpackage,
        exportConfig,
        toFTP,
      });

      await this.fetchExportToSAPLastExportInfo(selectedWorkpackage);
    },

    runExportPricing() {
      this.isExportPricingDialogOpen = false;
      const params = {
        selectedWorkpackage: this.workpackageToExportPricing,
        fields: this.fields,
        exportType: exportType.exportPricing,
        columnFormatters: this.columnFormatters,
      };
      this.downloadWorkpackageResults(params);
    },

    runExport() {
      if (this.useSapExport) return this.callDownloadExportToSAP();
      return this.runExportPricing();
    },

    openDeleteDialog(workpackage) {
      this.workpackageToDelete = workpackage;
      this.isDeleteDialogOpen = true;
    },

    closeDeleteDialog() {
      this.isDeleteDialogOpen = false;
      this.workpackageToDelete = undefined;
    },

    openTransferDialog(workpackage) {
      this.workpackageToTransfer = workpackage;
      this.isTransferDialogOpen = true;
    },

    closeTransferDialog() {
      this.isTransferDialogOpen = false;
      this.workpackageToTransfer = {};
    },

    async runTransferWorkpackage(event) {
      const { selectedFields, destinationWorkpackage, selectedCategories } = event;
      this.isTransferDialogOpen = false;
      this.transferWorkpackage({
        destinationWorkpackageId: destinationWorkpackage._id,
        sourceWorkpackageId: this.workpackageToTransfer._id,
        fields: selectedFields,
        selectedCategories,
      });
      this.workpackageToTransfer = {};
    },

    async runDeleteWorkpackage() {
      this.isDeleteDialogOpen = false;
      await this.deleteWorkpackage(this.workpackageToDelete);
    },

    isSelected(workpackage) {
      return workpackage._id === this.selectedWorkpackage._id ? 'selected' : '';
    },

    isActive(date, mocked) {
      return dateHasPassed(date) || mocked ? 'archived' : '';
    },

    getPeriodTypeBadge(workpackage) {
      const periodType = workpackage.workpackagePeriodType;
      if (periodType === workpackagePeriodTypes.weekly) {
        return {
          label: this.$t('workPackages.periodTypes.weeklyShort'),
          tooltip: this.$t('workPackages.periodTypes.weekly'),
        };
      }
      if (periodType === workpackagePeriodTypes.aoPeriod) {
        return {
          label: this.$t('workPackages.periodTypes.aoPeriodShort'),
          tooltip: this.$t('workPackages.periodTypes.aoPeriod'),
        };
      }
      return null;
    },

    getWorkpackageDescriptionTruncationLength(workpackage) {
      return this.getPeriodTypeBadge(workpackage)
        ? this.workpackageTruncationLength
        : this.workpackageTruncationLength + 6;
    },

    isExpanded(workpackageId) {
      const workpackage = find(this.allWorkpackages, { _id: workpackageId });
      return workpackage && workpackage.isExpanded === true;
    },

    valueRequired(v) {
      return !!v || v === 0 || this.$t('settings.parameters.errors.fieldInvalid');
    },

    hasValue(v) {
      return !!v || v === 0 || this.$t('settings.parameters.errors.fieldInvalid');
    },

    aoExtractsRules() {
      return (
        this.isAOExtractsValid(this.selectedAOExtracts) ||
        this.$t('validationErrors.periodIdsDatesDoNotMatch')
      );
    },

    getCompletionDate(completionDate) {
      return completionDate ? this.formatLocalisedDate(completionDate) : '-';
    },

    getReferenceDate(referenceDate) {
      return referenceDate ? this.formatLocalisedDate(referenceDate) : '-';
    },

    closeEditReferenceDateDialog() {
      this.isEditReferenceDateDialogOpen = false;
      // close workpackage to ensure updated referenceDate is displayed
      this.toggleExpansion(this.workpackageToEdit._id);
    },

    openEditReferenceDateDialog(workpackage) {
      this.workpackageToEdit = workpackage;
      this.referenceDatePickerEdit = false; // close datepicker
      this.isEditReferenceDateDialogOpen = true; // open confirmation modal
    },

    clearReferenceDate() {
      this.referenceDate = null;
    },

    clearReferenceDateAndOpenDialog(workpackage) {
      this.clearReferenceDate();
      this.openEditReferenceDateDialog(workpackage);
    },

    canUpdateReferenceDate(workpackage) {
      return (
        this.isExpanded(workpackage._id) &&
        this.canEditWorkpackage &&
        workpackage.type !== workpackageTypes.master
      );
    },

    async updateReferenceDate(workpackage, newReferenceDate) {
      if (workpackage.type === workpackageTypes.master) {
        // Cannot update reference date for master workpackage
        return;
      }

      const workpackageId = workpackage._id;
      const oldReferenceDate = workpackage.referenceDate;
      this.closeEditReferenceDateDialog();
      if (newReferenceDate === oldReferenceDate) {
        console.log('Reference date unchanged, not updating');
        return;
      }
      await this.updateWorkpackage({
        workpackage,
        updates: { referenceDate: newReferenceDate },
      });
      // workpackage update must complete before refreshing to ensure correct data is shown.
      if (this.isFeatureFlagEnabled(refreshWorkpackageFromDatabricks)) {
        await this.refreshWorkpackage({ workpackageId });
      }
    },

    canUpdateWorkpackageDescription(workpackage) {
      return (
        this.isExpanded(workpackage._id) &&
        this.canEditWorkpackage &&
        !workpackage.mocked &&
        workpackage.type !== workpackageTypes.master
      );
    },

    async updateWorkpackageDescription({ workpackage, description }) {
      const newDescription = description.trim();
      // Description is empty, not updating
      if (newDescription.length === 0) return;

      await this.updateWorkpackage({
        workpackage,
        updates: { description: newDescription },
      });
    },

    toggleExpansion(workpackageId) {
      const workpackage = find(this.allWorkpackages, { _id: workpackageId });
      if (!workpackage || get(workpackage, 'mocked')) return;

      this.editDescriptionBuffer = workpackage.description;
      const wasExpanded = workpackage.isExpanded;
      workpackage.isExpanded =
        workpackage.isExpanded === undefined ? true : !workpackage.isExpanded;

      if (!wasExpanded) {
        // only allow one expanded workpackage at a time
        // required for referenceDate edit
        this.workpackages.forEach((wp, index) => {
          if (wp.isExpanded && wp._id !== workpackageId && !get(wp, 'mocked')) {
            // close other expanded workpackages
            wp.isExpanded = false;
            this.$set(this.workpackages, index, wp);
          }
        });
      }

      const index = findIndex(this.allWorkpackages, { _id: workpackageId });
      this.$set(this.allWorkpackages, index, workpackage);

      if (workpackage.isExpanded) {
        this.fetchStoregroupKeyNameMap(workpackageId);
        if (this.enableReferenceDate) {
          this.referenceDate = get(workpackage, 'referenceDate');
        }
      }

      if (workpackage.isExpanded && !wasExpanded) {
        this.fetchExportToAPILastExportInfo(workpackage);
        this.fetchExportToSAPLastExportInfo(workpackage);
      }
    },

    toggleWorkpackagesExpansion() {
      this.workpackagesExpanded = !this.workpackagesExpanded;
      if (this.workpackagesExpanded) this.$emit('expand');
    },

    async selectWorkpackage(workpackage) {
      if (get(workpackage, 'mocked')) return;
      const workpackageId = workpackage._id;
      await this.setUserSelectionWorkpackage({ selectedWorkpackageId: workpackageId });
      if (this.isUnitManager && !this.isAdminLoggedIn) {
        this.$router.push({ path: `pricing/impact-view/workpackage/${workpackageId}` });
      } else {
        this.$router.push({ path: `pricing/desk-view/workpackage/${workpackageId}` });
      }
    },

    createNewWorkPackage() {
      this.resetNewWorkPackage();
      this.creationMode = true;
      this.$el.querySelector('.scroll').scrollTop = 0;
    },

    async saveNewWorkPackage() {
      this.newWorkpackage.owner = this.profile.username;
      this.newWorkpackage.hierarchyLevel = this.creationHierarchyLevel;
      this.newWorkpackage.completionDate = moment(this.completionDate).format(utcFormat);

      // YYYY-MM-DD format is required for analytics
      this.newWorkpackage.goLiveDate = moment(this.goLiveDate).format(yearMonthDayFormat);
      if (this.referenceDate) {
        this.newWorkpackage.referenceDate = moment(this.referenceDate).format(yearMonthDayFormat);
      }

      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        this.newWorkpackage.aoExtracts = this.selectedAOExtracts.map(id =>
          omit(this.getAOExtractsByKey[id], 'displayName')
        );
      }

      await this.createWorkpackage(this.newWorkpackage);
      // need to explicitly reset referenceDate, otherwise next creation will reuse old value
      this.referenceDate = null;
      this.creationMode = false;
    },

    cancelNewWorkPackage() {
      this.creationMode = false;
      this.resetNewWorkPackage();
    },

    setCategories(unit, categories) {
      const clonedSelectedCategories = clone(this.selectedCategories);
      clonedSelectedCategories[unit] = categories;
      this.selectedCategories = clonedSelectedCategories;

      let categoryIds = [];
      Object.keys(clonedSelectedCategories).forEach(function(key) {
        categoryIds = categoryIds.concat(clonedSelectedCategories[key]);
      });
      this.newWorkpackage.hierarchyIds = categoryIds;
    },

    setAvailableHierarchiesAndStoreFormat(selectedSourceWorkpackageId) {
      const selectedSourceWorkpackage = find(this.sourceWorkpackages, {
        _id: selectedSourceWorkpackageId,
      });
      this.newWorkpackage.storeFormat = selectedSourceWorkpackage.storeFormat;
      this.newWorkpackage.hierarchyIds = [];
      if (this.creationHierarchyLevel === categoryLevel) {
        this.setAvailableCategories(selectedSourceWorkpackage);
      } else if (this.creationHierarchyLevel === pricingGroupLevel) {
        this.setAvailablePricingGroups(selectedSourceWorkpackage);
      }
    },

    setAvailableCategories(workpackage) {
      this.availableCategories = this.getUnitsFromWorkPackage(workpackage, true);
      this.newWorkpackage.hierarchyLevel = this.creationHierarchyLevel;
      this.selectedCategories = {};
    },

    setAvailablePricingGroups(workpackage) {
      this.availablePricingGroups = this.getHierarchyForWorkpackage(
        {
          level: pricingGroupLevel,
        },
        workpackage._id
      );
      this.newWorkpackage.hierarchyLevel = this.creationHierarchyLevel;
    },

    resetNewWorkPackage() {
      const goLiveDate = new Date().toISOString().substr(0, 10);
      const completionDate = this.getCompletionDateBasedOnGoLiveDate({ goLiveDate }) || goLiveDate;

      this.newWorkpackage = {
        goLiveDate,
        completionDate,
        storeFormat: undefined,
        description: undefined,
        owner: undefined,
        hierarchyIds: undefined,
        workpackageId: undefined,
      };
      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        this.newWorkpackage.aoExtracts = [];
        this.selectedAOExtracts = [];
      }
      if (this.isFeatureFlagEnabled(enableReferenceDate)) {
        this.referenceDate = null;
      }
      if (this.isFeatureFlagEnabled(enableCompetitorSimulation)) {
        this.$set(this.newWorkpackage, 'costChangeThresholds', {
          min: this.toggleLogic[defaultCostChangeThresholdMin],
          max: this.toggleLogic[defaultCostChangeThresholdMax],
        });
        this.hasThresholdValidationError = false;
      }
      if (this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes)) {
        this.$set(this.newWorkpackage, 'workpackagePeriodType', defaultWorkpackagePeriodType);
      }
      this.availableCategories = [];
      this.selectedCategories = {};
      this.availablePricingGroups = [];
      this.goLiveDate = goLiveDate;
      this.completionDate = completionDate;
      this.defaultCreateWorkpackageOptions();
    },

    getUnitsFromWorkPackage(item, creation = false) {
      const retailUnits = get(item, 'units', []).filter(unit => unit.wholesale === false);
      if (creation) {
        return sortBy(retailUnits, 'levelEntryDescription');
      }
      return sortBy(retailUnits, 'levelEntryDescription').slice(0, this.unitsToDisplay);
    },

    onGoLiveDateInput() {
      this.goLiveDatePicker = false;
      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        this.selectedAOExtracts = [];
      }
    },

    async handleExportToAPI() {
      await this.exportWorkpackageResultsToAPI({
        selectedWorkpackage: this.workpackageToExportAPI,
        apiExportFields: this.apiExportFields,
      });

      await this.fetchExportToAPILastExportInfo(this.workpackageToExportAPI);

      this.closeExportAPIDialog();
    },

    async fetchExportLastInfo(workpackage, type) {
      if (!workpackage || !workpackage._id) return;

      const workpackageId = workpackage._id;
      const exportTypeKey = type === exportType.exportToSAP ? 'sap' : 'api';

      if (!this.isExportLastInfoLoading[workpackageId]) {
        this.$set(this.isExportLastInfoLoading, workpackageId, {});
      }
      if (!this.exportLastInfo[workpackageId]) {
        this.$set(this.exportLastInfo, workpackageId, {});
      }

      this.$set(this.isExportLastInfoLoading[workpackageId], exportTypeKey, true);

      try {
        const whereClause = {
          fullEventName: Events.workpackage.exportResults,
          entityIds: { $in: [workpackageId] },
          'extra.responseStatusCode': 200,
          'extra.queryParams.exportType': { $exists: false },
        };

        if (type === exportType.exportToSAP) {
          whereClause['extra.queryParams.exportType'] = String(exportType.exportToSAP);
          whereClause['extra.toFTP'] = true;
        }

        const params = {
          where: whereClause,
          sortBy: 'date',
          sortDirection: descending,
          limit: 1,
        };

        const { data: auditLogs } = await axios.get('/api/audit-log', { params });
        const result = !isEmpty(auditLogs) ? auditLogs[0] : null;

        // Store result
        this.$set(this.exportLastInfo[workpackageId], exportTypeKey, result);
      } catch (error) {
        console.error(`Error fetching last ${exportTypeKey} export info:`, error);
        this.$set(this.exportLastInfo[workpackageId], exportTypeKey, null);
      } finally {
        this.$set(this.isExportLastInfoLoading[workpackageId], exportTypeKey, false);
      }
    },

    async fetchExportToAPILastExportInfo(workpackage) {
      if (!workpackage || !workpackage._id) return;
      await this.fetchExportLastInfo(workpackage);
    },

    async fetchExportToSAPLastExportInfo(workpackage) {
      if (!workpackage || !workpackage._id) return;
      await this.fetchExportLastInfo(workpackage, exportType.exportToSAP);
    },

    getExportLastText(workpackageId, type) {
      const exportTypeKey = type === exportType.exportToSAP ? 'sap' : 'api';
      const info = get(this.exportLastInfo, [workpackageId, exportTypeKey]);
      if (!info) return '';

      return this.$t('workPackages.lastExport', {
        date: moment(info.date).format(hoursMinutesDayMonthYearSeparatedFormat),
        username: info.username,
      });
    },

    hasExportInfo(workpackageId, type) {
      const exportTypeKey = type === exportType.exportToSAP ? 'sap' : 'api';
      return !!get(this.exportLastInfo, [workpackageId, exportTypeKey]);
    },

    openWorkpackageCopyDialog(workpackage) {
      if (!workpackage) {
        return;
      }

      this.defaultCreateWorkpackageOptions();
      this.setExactCopyWorkpackageOptions();
      this.isCopyWorkpackageDialogOpen = true;
      this.copyWorkpackageOptions = {
        reuseExistingAOExtracts: true,
        source: workpackage,
        target: {
          workpackageId: workpackage._id,
          description: this.$t('workPackages.copyOf', { workpackageName: workpackage.description }),
          storeFormat: workpackage.storeFormat,
          completionDate: moment(workpackage.completionDate).format(yearMonthDayFormat),
          goLiveDate: workpackage.goLiveDate,
          referenceDate: workpackage.referenceDate
            ? moment(workpackage.referenceDate).format(yearMonthDayFormat)
            : null,
          costChangeThresholds: {
            // copy thresholds from source if they exist, otherwise set to null
            min: get(workpackage, 'costChangeThresholds.min', null),
            max: get(workpackage, 'costChangeThresholds.max', null),
          },
          workpackagePeriodType: workpackage.workpackagePeriodType || null,
        },
        selectedAOExtracts: workpackage.aoExtracts
          ? workpackage.aoExtracts.map(extract => extract.auditCorrelationId)
          : [],
      };
    },

    async confirmCopyWorkpackage({ isExactCopy }) {
      this.isCopyWorkpackageDialogOpen = false;

      if (isExactCopy) {
        const source = this.copyWorkpackageOptions.source;
        const description = this.copyWorkpackageOptions.target.description;
        await this.cloneWorkpackage({ workpackage: source, description });
        this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
        return;
      }

      // copy target to avoid datepicker Error in render: "RangeError: Invalid time value" in copyWorkpackageDialog
      const newWorkpackage = { ...this.copyWorkpackageOptions.target };

      newWorkpackage.isCopy = true;
      newWorkpackage.owner = this.profile.username;
      newWorkpackage.hierarchyLevel = this.creationHierarchyLevel;

      const sourceId = this.copyWorkpackageOptions.source._id;
      if (!this.hierarchy[sourceId]) {
        const [fetchErr] = await to(
          this.fetchHierarchyForWorkpackage({ params: { where: { workpackageId: sourceId } } })
        );
        if (fetchErr) {
          console.error('Failed to fetch hierarchy for workpackage copy', fetchErr);
          this.notifyCopyWorkpackageFailed(newWorkpackage.description);
          this.isCopyWorkpackageDialogOpen = false;
          this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
          return;
        }
      }

      const hierarchyIds = this.getHierarchyForWorkpackage(
        { level: this.creationHierarchyLevel },
        sourceId
      ).map(hierarchy => hierarchy.levelEntryKey);

      if (!hierarchyIds.length) {
        console.error(
          'Hierarchy not available for workpackage copy — aborting to prevent data loss',
          { sourceId }
        );
        this.notifyCopyWorkpackageFailed(newWorkpackage.description);
        this.isCopyWorkpackageDialogOpen = false;
        this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
        return;
      }

      newWorkpackage.hierarchyIds = hierarchyIds;

      newWorkpackage.completionDate = moment(newWorkpackage.completionDate).format(utcFormat);

      // YYYY-MM-DD format is required for analytics
      newWorkpackage.goLiveDate = moment(newWorkpackage.goLiveDate).format(yearMonthDayFormat);

      if (newWorkpackage.referenceDate) {
        newWorkpackage.referenceDate = moment(newWorkpackage.referenceDate).format(
          yearMonthDayFormat
        );
      }

      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        if (this.copyWorkpackageOptions.reuseExistingAOExtracts) {
          if (!isEmpty(this.copyWorkpackageOptions.source.aoExtracts)) {
            // reuse existing AO extracts
            // filter existing extracts by new target workpackage go live date
            const newAvailableAOExtracts = this.getAvailableAOExtracts(newWorkpackage.goLiveDate);
            const filteredAOExtracts = this.copyWorkpackageOptions.source.aoExtracts.filter(
              aoExtract =>
                newAvailableAOExtracts.some(
                  e => e.auditCorrelationId === aoExtract.auditCorrelationId
                )
            );
            newWorkpackage.aoExtracts = filteredAOExtracts;
          }
        } else {
          // user selected new AO extracts manually
          newWorkpackage.aoExtracts = this.copyWorkpackageOptions.selectedAOExtracts.map(id =>
            omit(this.getAOExtractsByKey[id], 'displayName')
          );
        }
      }

      await this.createWorkpackage(newWorkpackage);

      this.isCopyWorkpackageDialogOpen = false;
      this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
    },

    cancelCopyWorkpackage() {
      this.isCopyWorkpackageDialogOpen = false;
      this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
    },

    getColSpan(baseColSpan) {
      if (this.enableReferenceDate) {
        baseColSpan += 1;
      }
      return baseColSpan;
    },

    /**
     * Parse formatted text into components (extracts bold text marked with **text**).
     * @param {string} text - The text to parse.
     * @returns {Object} Object with plain and bold text parts.
     */
    parseFormattedText(text) {
      const match = text.match(/^(.*?)\*\*(.*?)\*\*(.*)$/);
      if (match) {
        return {
          plain: match[1],
          bold: match[2],
        };
      }
      return { plain: text, bold: null };
    },

    /**
     * Commits a failed_to_send notification for workpackage copy.
     * @param {string} description - The target workpackage description shown in the toast.
     */
    notifyCopyWorkpackageFailed(description) {
      this.$store.commit('notifications/addNotification', {
        id: `run_create_workpackage:failed_to_send:${Date.now()}`,
        jobStatus: 'failed_to_send',
        notificationPayload: description,
        baseTranslation: 'notifications.run_create_workpackage',
      });
    },

    openCloneDialog(workpackage) {
      this.workpackageToClone = workpackage;
      this.isCloneConfirmDialogOpen = true;
    },

    confirmClone() {
      this.cloneWorkpackage({ workpackage: this.workpackageToClone });
      this.closeCloneDialog();
    },

    closeCloneDialog() {
      this.isCloneConfirmDialogOpen = false;
      this.workpackageToClone = {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.search-container {
  height: 4.5rem !important;
  border: none !important;
}

.search-field {
  position: absolute;
  min-width: 250px;
}

%sticky-column {
  position: -webkit-sticky;
  /* for Safari */
  position: sticky;
  z-index: 1;
}

@mixin column-width($width) {
  min-width: $width;
  width: $width;
}

#dialog-warning,
.dialog-warning {
  color: $pricing-red;
}

.scroll {
  max-height: 50rem;
  overflow: scroll;
}

.work-package-table.v-data-table ::v-deep {
  overflow: scroll;
  /* Prevents horizontal overscroll from triggering browser back/forward navigation. */
  overscroll-behavior-x: contain;
  position: relative;
  max-width: 99%;

  .content-end.col-border-right {
    -webkit-box-shadow: 4px 0px 0px 0px rgba(0, 0, 0, 0.14);
    -moz-box-shadow: 4px 0px 0px 0px rgba(0, 0, 0, 0.14);
    box-shadow: 4px 0px 0px 0px rgba(0, 0, 0, 0.14);
  }

  .content-end.col-border-left {
    -webkit-box-shadow: -4px 0px 0px 0px rgba(0, 0, 0, 0.14);
    -moz-box-shadow: -4px 0px 0px 0px rgba(0, 0, 0, 0.14);
    box-shadow: -4px 0px 0px 0px rgba(0, 0, 0, 0.14);
  }

  .col-border-left {
    border-left: 0.1rem solid $pricing-grey-dark !important;
  }

  $first-column-width: 27rem;
  $dates-column-width: 10rem;
  $first-column-offset: 0;

  .v-data-table__wrapper {
    table {
      border-collapse: separate;
      /* Don't collapse */
      $second-column-width: $dates-column-width;
      $third-column-width: $dates-column-width;
      $second-column-offset: $first-column-offset + $first-column-width;
      $third-column-offset: $second-column-offset + $second-column-width;

      thead th,
      tbody td {
        &:nth-child(1) {
          @extend %sticky-column;
          @include column-width($first-column-width);
          left: $first-column-offset;
        }

        &:nth-child(2) {
          @extend %sticky-column;
          @include column-width($second-column-width);
          left: $second-column-offset;
        }

        &:last-child {
          @extend %sticky-column;
          @include column-width(6rem);
          right: 0;
        }
      }

      thead th {
        &:nth-child(3) {
          @extend %sticky-column;
          @include column-width($third-column-width);
          left: $third-column-offset;
        }
      }

      thead {
        tr {
          height: 2.2rem;
          font-size: 2.2rem;
        }

        th {
          transform: scale(0.999);
          background-color: $pricing-background;
          padding: 0 0.5rem;
          height: unset;

          &:not(:last-child) {
            border-right: 0.1rem solid $pricing-grey-dark;
          }
        }

        .v-datatable__progress {
          background-color: $pricing-grey-dark;
        }
      }

      tbody {
        tr {
          &.workpackage td:not(.col-no-border) {
            border-top: 0.2rem solid $pricing-grey-dark;
          }

          &.selected td:not(.col-no-border) {
            border-top: 0.2rem solid $workPackagesStatusPending;
          }

          &.white-row td {
            background: $pricing-white;
          }

          &.spacer-row {
            background: $pricing-background;
          }

          &.spacer-row:last-child {
            td {
              border: 0;
              -webkit-box-shadow: unset;
              -moz-box-shadow: unset;
              box-shadow: unset;
            }
          }

          &.button-row td {
            background: $pricing-white;
            text-align: right;
          }
        }

        td {
          color: $workPackagesTableTextColor;
          height: 2.2rem;
          font-size: 1.4rem;
          vertical-align: top;

          &:not(.col-no-border) {
            border-bottom: 0.1rem solid $pricing-grey-dark;
          }

          &.text-small {
            font-size: 1.2rem;
          }

          &.text-bold {
            font-weight: 600;
          }

          &.col-fixed-width {
            min-width: $dates-column-width;
          }

          &.col-unit {
            min-width: 31rem;

            .pg-autocomplete {
              .v-label.v-label--active {
                margin-top: -0.5rem;
              }
              .v-chip {
                margin-top: 0.5rem;
              }
            }
          }

          &.col-border-right:not(:last-child) {
            border-right: 0.1rem solid $pricing-grey-dark !important;
          }

          &:first-child {
            vertical-align: top;
            padding: 5px 10px;
          }

          .inner-table td {
            min-width: $dates-column-width;
            max-width: $dates-column-width;
            border-bottom: unset;
          }
        }
      }
    }
  }

  &.reference-date-enabled {
    $second-column-width: $dates-column-width * 2;
    $third-column-width: $dates-column-width;
    $second-column-offset: $first-column-offset + $first-column-width;
    $third-column-offset: $second-column-offset + $second-column-width;

    // tbody and thead count do not match. date cols use sub-table, so n-th child 2 now has 3 th for 1 td
    table {
      tbody td {
        &:nth-child(2) {
          @extend %sticky-column;
          @include column-width($second-column-width);
          left: $second-column-offset;
        }
      }

      thead th {
        &:nth-child(4) {
          @extend %sticky-column;
          @include column-width($dates-column-width);
          left: $third-column-offset;
        }
      }
    }
  }

  .workpackage.archived {
    color: $pricing-grey-dark !important;

    a {
      color: $pricing-grey-dark !important;
    }

    .v-icon {
      color: $pricing-grey-dark;
    }
  }

  .period-type-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    min-width: 3.2rem;
    padding: 0.2rem 0.6rem;
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.15;
    color: #343a3f;
    background: #eceff1;
    border: 1px solid #cfd4d9;
    border-radius: 4px;
    cursor: default;
  }

  .workpackage-description-row {
    display: flex;
    align-items: flex-start;

    /* v-tooltip wrapper defaults can offset the badge vs. the description link */
    > .v-tooltip {
      display: inline-flex;
      align-items: flex-start;
      flex-shrink: 0;
      line-height: 1;
    }
  }

  .workpackage-description-content {
    flex: 1 1 auto;
    min-width: 0;
    line-height: 1.15;
  }

  .no-data-text {
    width: 29rem;
    font-size: 1.2rem;
    padding: 1rem 0;
    white-space: normal;
  }

  .v-text-field {
    padding: unset;
    margin: unset;
  }

  .new-workpackage,
  .edit-reference-date {
    .v-icon {
      font-size: 1.5rem;
    }
  }

  .new-workpackage-buttons {
    min-width: 20rem !important;
  }
}

// not enough space for both event (calendar) and clearable icon.
// on hover hide event icon, show clearable icon
::v-deep .edit-reference-date .v-input__icon--clear {
  display: none;
}
::v-deep .edit-reference-date:hover .v-input__append-inner {
  // hide event calendar icon on hover, make space for clearable icon
  .v-input__icon--append {
    display: none;
  }
  // show clearable icon
  .v-input__icon--clear {
    display: inline-flex;
  }
}

.match-text-input-padding-top {
  padding-top: 10px !important;
}

.panel-header {
  font-size: 1.4rem;
}

.ao-extracts {
  max-width: 300px;
}

.api-export-container {
  vertical-align: middle;
}

.last-export-info {
  margin: 0 8px;
  max-width: 110px;
  font-size: 0.85rem;
  color: $pricing-grey-dark;
  text-wrap: auto;
}
</style>
